'use client';

import { createContext, type ReactNode, useContext } from 'react';

/**
 * Server-resolved `agents-membership` flag (see `src/flags.ts` and Phase H
 * of docs/plans/2026-09-17-agentic-rag-and-conversations.md), threaded down
 * to every client component that decides whether to show agents in a
 * picker or membership control. The flag is evaluated once per request in
 * the organization layout (server-only, never trusted from a client-sent
 * value) -- this context only carries that already-resolved boolean, it
 * never re-evaluates the flag itself. Off means every consumer renders
 * exactly as it did before agents existed.
 */
const AgentsMembershipContext = createContext(false);

export function AgentsMembershipProvider({
  enabled,
  children
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  return (
    <AgentsMembershipContext.Provider value={enabled}>
      {children}
    </AgentsMembershipContext.Provider>
  );
}

/** Whether agents may appear in pickers/membership controls for this organization. */
export function useAgentsMembershipEnabled(): boolean {
  return useContext(AgentsMembershipContext);
}
